// src/ApiPanel/constants/apiPanelNav.js
// Flat top-level list (matches the reference panel — no section dividers,
// just one continuous list, each item optionally expandable). Items with a
// `children` array render as a collapsible group in the sidebar; items with
// only a `path` render as a direct link.
//
// Only the sections actually confirmed from screenshots (Transaction
// Report, Token & API Request, CA Report, GST Report) have real submenus
// below. Every other item is a single link for now — give it a `children`
// array in the exact same shape whenever you have its real submenu, and
// both the sidebar and the router pick it up automatically, no other code
// changes needed.

export const API_PANEL_NAV = [
  { key: "dashboard", label: "Dashboard", icon: "iconoir-home-alt", path: "/api-panel/dashboard" },

  { key: "complaint", label: "Complaint", icon: "iconoir-chat-bubble-question", path: "/api-panel/complaint" },

  { key: "website-payments", label: "Website Payments", icon: "iconoir-credit-card", path: "/api-panel/website-payments" },

  {
    key: "token-api-request",
    label: "Token & API Request",
    icon: "iconoir-key-alt-plus",
    children: [
      { key: "token", label: "Token", path: "/api-panel/tokens/manage" },
      { key: "api-integration-request", label: "Api Integration Request", path: "/api-panel/tokens/integration-request" },
    ],
  },

  { key: "bank-info", label: "Bank Info", icon: "iconoir-bank", path: "/api-panel/bank-info" },

  {
    key: "transaction-report",
    label: "Transaction Report",
    icon: "iconoir-page-flip",
    // Reordered so the reports that have a working endpoint come first. The
    // tail of this list is the items that came from the reference panel but
    // have nothing behind them in this API yet — they render a page that says
    // so rather than an empty table, which would look identical to a report
    // with no transactions.
    children: [
      { key: "prepaid-dth-report", label: "PrePaid & DTH Report", path: "/api-panel/reports/transactions/prepaid-dth" },
      { key: "bill-payment-report", label: "Bill Payment Report", path: "/api-panel/reports/transactions/bill-payment" },
      { key: "bill-payment-draft", label: "Bill Payment Draft", path: "/api-panel/reports/transactions/bill-payment-draft" },
      { key: "aeps-report", label: "AEPS Report", path: "/api-panel/reports/transactions/aeps" },
      { key: "matm-report", label: "Micro ATM Report", path: "/api-panel/reports/transactions/matm" },
      { key: "dmt-report", label: "DMT Report", path: "/api-panel/reports/transactions/dmt" },
      { key: "upi-qr-report", label: "UPI QR Report", path: "/api-panel/reports/transactions/upi-qr" },
      { key: "credit-debit-report", label: "Credit / Debit Report", path: "/api-panel/reports/transactions/credit-debit" },
      { key: "pancard-txn-history", label: "PAN CARD TXN History", path: "/api-panel/reports/transactions/pancard-txn-history" },

      { key: "payout-report", label: "PAYOUT Report", path: "/api-panel/reports/transactions/payout" },
      { key: "recharge-dispute", label: "Recharge Dispute", path: "/api-panel/reports/transactions/recharge-dispute" },
      { key: "cash-deposit-report", label: "Cash Deposit Report", path: "/api-panel/reports/transactions/cash-deposit" },
      { key: "manual-pancard-request", label: "Manual Pancard Request", path: "/api-panel/reports/transactions/manual-pancard-request" },
      { key: "money-succ-to-failed", label: "Money Succ_to_failed", path: "/api-panel/reports/transactions/money-succ-to-failed" },
      { key: "money-failed-to-succ", label: "Money Failed_to_succ", path: "/api-panel/reports/transactions/money-failed-to-succ" },
      { key: "aadhar-verify-report", label: "Aadhar Verify Report", path: "/api-panel/reports/transactions/aadhar-verify" },
      { key: "pan-verify-report", label: "Pan Verify Report", path: "/api-panel/reports/transactions/pan-verify" },
      { key: "axis-account-open", label: "Axis Account Open", path: "/api-panel/reports/transactions/axis-account-open" },
      { key: "kotak-qr-report", label: "Kotak Qr Report", path: "/api-panel/reports/transactions/kotak-qr" },
      { key: "norton-report", label: "Norton Report", path: "/api-panel/reports/transactions/norton" },
      { key: "fino-cms-report", label: "Fino CMS Report", path: "/api-panel/reports/transactions/fino-cms" },
    ],
  },

  { key: "cards", label: "Cards", icon: "iconoir-credit-card-slash", path: "/api-panel/cards" },
  { key: "aeps-upi", label: "AEPS UPI", icon: "iconoir-smartphone-device", path: "/api-panel/services/aeps-upi" },
  { key: "aeps", label: "AEPS", icon: "iconoir-fingerprint", path: "/api-panel/services/aeps" },
  { key: "micro-atm", label: "Micro ATM", icon: "iconoir-atm", path: "/api-panel/services/micro-atm" },
  { key: "account", label: "Account", icon: "iconoir-wallet", path: "/api-panel/account" },

  {
    key: "ca-report",
    label: "CA Report",
    icon: "iconoir-page-flip",
    children: [
      { key: "monthly-ledger", label: "Monthly Ledger", path: "/api-panel/ca-report/monthly-ledger" },
      { key: "sell-cms-invoice-details", label: "Sell CMS Invoice Details", path: "/api-panel/ca-report/sell-cms-invoice-details" },
      { key: "sell-invoice-details", label: "Sell Invoice Details", path: "/api-panel/ca-report/sell-invoice-details" },
      { key: "purchase-invoice-details", label: "Purchase Invoice Details", path: "/api-panel/ca-report/purchase-invoice-details" },
      { key: "sell-recharge-invoice-details", label: "Sell Recharge Invoice Details", path: "/api-panel/ca-report/sell-recharge-invoice-details" },
      { key: "purchase-recharge-invoice-details", label: "Purchase Recharge Invoice Details", path: "/api-panel/ca-report/purchase-recharge-invoice-details" },
      { key: "purchase-utility-invoice-details", label: "Purchase Utility Invoice Details", path: "/api-panel/ca-report/purchase-utility-invoice-details" },
      { key: "tds", label: "TDS", path: "/api-panel/ca-report/tds" },
      { key: "cash-declaration", label: "Cash Declaration", path: "/api-panel/ca-report/cash-declaration" },
      { key: "upload-gst", label: "Upload GST", path: "/api-panel/ca-report/upload-gst" },
      { key: "upload-gst-recharge", label: "Upload GST Recharge", path: "/api-panel/ca-report/upload-gst-recharge" },
      { key: "upload-cash-declaration", label: "Upload Cash Declaration", path: "/api-panel/ca-report/upload-cash-declaration" },
    ],
  },

  {
    key: "gst-report",
    label: "GST Report",
    icon: "iconoir-percentage-circle",
    children: [
      { key: "gst-information", label: "GST Information", path: "/api-panel/gst-report/information" },
      { key: "gst-pay-report", label: "GST PAY Report", path: "/api-panel/gst-report/pay-report" },
      { key: "gst-reversal-report", label: "GST Reversal Report", path: "/api-panel/gst-report/reversal-report" },
    ],
  },

  { key: "purchase-order", label: "Purchase Order", icon: "iconoir-cart", path: "/api-panel/purchase-order" },
  { key: "offline-utility", label: "Offline Utility Recharge", icon: "iconoir-flash", path: "/api-panel/services/offline-utility" },
  { key: "code-commission", label: "Code & Commission", icon: "iconoir-git-fork", path: "/api-panel/code-commission" },
  { key: "m-pos", label: "M-POS", icon: "iconoir-shop", path: "/api-panel/services/m-pos" },
  { key: "hotel", label: "Hotel", icon: "iconoir-building", path: "/api-panel/travel/hotel" },
  { key: "irctc", label: "IRCTC", icon: "iconoir-train", path: "/api-panel/travel/irctc" },
  { key: "bus", label: "Bus", icon: "iconoir-bus", path: "/api-panel/travel/bus" },
  { key: "flight", label: "Flight", icon: "iconoir-airplane", path: "/api-panel/travel/flight" },
];

// Recursively walk the nav and return every leaf item (one with a `path`,
// regardless of nesting depth). Used by the router to auto-generate a stub
// route for anything that doesn't have a real page yet.
export function flattenNavLeaves(nav = API_PANEL_NAV) {
  const leaves = [];
  for (const item of nav) {
    if (item.children?.length) {
      leaves.push(...flattenNavLeaves(item.children));
    } else if (item.path) {
      leaves.push(item);
    }
  }
  return leaves;
}
